import { localDateTimeOf, zonedWallTimeToInstant } from "./time-zone.js";
// Weather risk of itinerary items (CHT-745; contract `docs/architecture/milestone-d-contracts.md`, sections 4.4, 4.5 and
// 5.2). Pure: no I/O, no clock, no provider. A forecast is time-bounded EVIDENCE, never a Lafayette fact. Nothing here
// cancels an item, changes catalog availability, an item status or a request: the strongest result is a proposal that
// the customer confirms (contract 3.6). The model may not say that a venue is closed because of weather.
//
// A `null` number is UNKNOWN, never zero: MET Norway has no precipitation probability and no gust, and an unknown
// factor never triggers a level. It is listed in `unknownFactors` instead.
/** How an item reacts to weather. `NONE` never fetches a forecast. */
export const weatherSensitivities = Object.freeze(["NONE", "OUTDOOR", "WATER", "HEIGHT"]);
/** A closed Lafayette code for the sky. Never the provider's own text. */
export const weatherSummaryCodes = Object.freeze([
    "CLEAR",
    "PARTLY_CLOUDY",
    "CLOUDY",
    "FOG",
    "DRIZZLE",
    "RAIN",
    "HEAVY_RAIN",
    "SHOWERS",
    "SNOW",
    "SLEET",
    "HAIL",
    "THUNDERSTORM",
    "UNKNOWN",
]);
export const weatherRiskLevels = Object.freeze(["NONE", "WATCH", "HIGH", "SEVERE"]);
/**
 * What the product does about a level. `NONE` (no risk) is an addition to the four values of contract 5.2, so that every
 * assessment has an outcome.
 */
export const weatherOutcomes = Object.freeze([
    "NONE",
    "INFO_NOTE",
    "SUGGEST_ALTERNATIVE",
    "CONCIERGE_REVIEW",
    "UNVERIFIED",
]);
/** The facts that decide a level. A factor is a code, never a provider field name. */
export const weatherFactors = Object.freeze([
    "PRECIP_PROBABILITY",
    "PRECIP_AMOUNT",
    "WIND_GUST",
    "THUNDERSTORM",
    "HAIL",
]);
/** Why a check could not say anything. Stored as a code, never as provider text. */
export const weatherUnverifiedReasons = Object.freeze([
    "OUT_OF_RANGE",
    "NO_OWNED_POINT",
    "NO_COVERAGE",
    "NO_DECIDING_DATA",
    /** The usable intervals cover only part of the activity and nothing known in them is a risk: never certified as calm. */
    "PARTIAL_COVERAGE",
    "PROVIDER_UNAVAILABLE",
    "QUOTA",
    "TIMEOUT",
    "INVALID_REQUEST",
    "NOT_CONFIGURED",
]);
export const weatherEvidenceStates = Object.freeze([
    "VERIFIED",
    "UNVERIFIED",
    "OUT_OF_RANGE",
]);
/** The providers a weather evidence row may name. */
export const weatherEvidenceProviders = Object.freeze(["google", "met_no", "fake"]);
/**
 * The thresholds of contract 4.4. They are PRODUCT JUDGEMENT, not official safety limits and not warnings of a weather
 * service. The highest matching level wins. `precipMmPerHour` is an HOURLY threshold: it applies only to an interval that is
 * one hour long. The amount of a longer interval (a six-hour total) says nothing about its worst hour, so the factor is
 * UNKNOWN there (listed in `unknownFactors`), never an average. The other factors still apply.
 */
export const WEATHER_POLICY = Object.freeze({
    watch: Object.freeze({ precipProbabilityPct: 30, gustKph: 40 }),
    high: Object.freeze({
        precipProbabilityPct: 60,
        precipMmPerHour: 2.5,
        gustKph: 60,
        thunderstormProbabilityPct: 50,
    }),
    severe: Object.freeze({ gustKph: 90, thunderstormProbabilityPct: 80 }),
    /** The length of an item that has neither an end nor a duration. */
    defaultItemMinutes: 60,
    /** The longest window one forecast call covers (the port limit). */
    maxWindowHours: 48,
    /** The longest interval that an hourly rain amount can be judged on, in minutes (one hour). */
    hourlyIntervalMinutes: 60,
    /** A stored VERIFIED answer is reused this long when the point, the window and the provider are the same. */
    reuseMinutes: 180,
});
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;
// --- Sensitivity ----------------------------------------------------------------------------------------------------------
const OUTDOOR_TAGS = new Set([
    "outdoor",
    "open_air",
    "al_fresco",
    "terrace",
    "rooftop",
    "garden",
    "beach",
    "beachfront",
    "walking_tour",
    "hiking",
    "safari",
    "picnic",
]);
const WATER_TAGS = new Set([
    "water_sports",
    "boat",
    "boat_tour",
    "sailing",
    "snorkelling",
    "snorkeling",
    "diving",
    "kayaking",
    "surfing",
    "swimming",
]);
const HEIGHT_TAGS = new Set([
    "zip_line",
    "climbing",
    "abseiling",
    "bungee",
    "paragliding",
    "hot_air_balloon",
    "cable_car",
    "rope_course",
]);
const INDOOR_TAGS = new Set(["indoor", "covered", "enclosed"]);
/** Catalog types whose tags describe what the customer does. A villa's garden or a service's tag is an amenity, not an outing. */
const ACTIVITY_CATALOG_TYPES = new Set([
    "EVENT",
    "EXPERIENCE",
    "DINING",
]);
/** Item kinds that never react to weather: travel and buffers. */
const NEVER_SENSITIVE_KINDS = new Set(["TRANSIT", "BUFFER"]);
/**
 * The item kinds in which the PLANNER (the agent) may set `OUTDOOR` itself (contract 5.2). Anything else is the customer's or
 * staff's to set. The planner may set no other sensitivity.
 */
export const PLANNER_OUTDOOR_ITEM_KINDS = Object.freeze([
    "EXTERNAL_PLACE",
    "CUSTOM_ACTIVITY",
]);
/**
 * The sensitivity of an item. A catalog activity (an event, an experience, dining) is read from its tags first: height before
 * water before outdoor. An explicit indoor tag makes it `NONE`, whatever the item's own column says (a valid indoor
 * alternative). A catalog item with no verdict, and every other item, uses the item's own `weather_sensitivity`. Travel and
 * buffers are never sensitive.
 */
export function weatherSensitivityOf(input) {
    if (NEVER_SENSITIVE_KINDS.has(input.kind))
        return "NONE";
    const catalog = input.catalog;
    if (catalog !== null && ACTIVITY_CATALOG_TYPES.has(catalog.itemType)) {
        const codes = new Set(catalog.tagCodes.map((code) => code.trim().toLowerCase()));
        const has = (set) => [...codes].some((code) => set.has(code));
        if (has(HEIGHT_TAGS))
            return "HEIGHT";
        if (has(WATER_TAGS))
            return "WATER";
        if (has(OUTDOOR_TAGS))
            return "OUTDOOR";
        if (has(INDOOR_TAGS))
            return "NONE";
    }
    return input.own;
}
/**
 * The UTC window the weather of an item is read for: its start to its end (the end, else the start plus the duration, else
 * `WEATHER_POLICY.defaultItemMinutes`), at most `maxWindowHours` long. `null` for an item with no start (untimed or all day).
 */
export function weatherWindowOf(item, policy = WEATHER_POLICY) {
    if (item.timingKind !== "SCHEDULED" || item.startsAt === null)
        return null;
    const start = item.startsAt.getTime();
    let end;
    if (item.endsAt !== null)
        end = item.endsAt.getTime();
    else if (item.durationMinutes !== null)
        end = start + item.durationMinutes * MINUTE_MS;
    else
        end = start + policy.defaultItemMinutes * MINUTE_MS;
    if (end <= start)
        end = start + policy.defaultItemMinutes * MINUTE_MS;
    end = Math.min(end, start + policy.maxWindowHours * HOUR_MS);
    return { from: new Date(start), to: new Date(end) };
}
const LEVEL_RANK = {
    NONE: 0,
    WATCH: 1,
    HIGH: 2,
    SEVERE: 3,
};
const higher = (a, b) => LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b;
const OUTCOME_OF_LEVEL = {
    NONE: "NONE",
    WATCH: "INFO_NOTE",
    HIGH: "SUGGEST_ALTERNATIVE",
    SEVERE: "CONCIERGE_REVIEW",
};
export const weatherOutcomeOf = (level) => OUTCOME_OF_LEVEL[level];
const FACTOR_ORDER = new Map(weatherFactors.map((factor, index) => [factor, index]));
export const weatherUnverified = (reason, state = "UNVERIFIED", coverage = null) => Object.freeze({
    state,
    level: null,
    factors: [],
    unknownFactors: [],
    outcome: "UNVERIFIED",
    reason,
    summary: null,
    coverage,
});
/** The hourly rain amount of an interval, or `null`: only an interval of one hour says what fell in an hour. */
const hourlyRainOf = (interval, policy) => {
    if (interval.precipMm === null)
        return null;
    const minutes = (interval.end.getTime() - interval.start.getTime()) / MINUTE_MS;
    return minutes > policy.hourlyIntervalMinutes ? null : interval.precipMm;
};
/**
 * The union of `[from, to]` of the usable intervals, clipped to the window: minutes covered, and the periods left uncovered.
 * Pure. Two overlapping intervals count once.
 */
function coverageOf(window, usable) {
    const spans = usable
        .map((interval) => ({
        from: Math.max(interval.start.getTime(), window.from.getTime()),
        to: Math.min(interval.end.getTime(), window.to.getTime()),
    }))
        .filter((span) => span.to > span.from)
        .sort((a, b) => a.from - b.from);
    const uncovered = [];
    let reached = window.from.getTime();
    let covered = 0;
    for (const span of spans) {
        if (span.from > reached)
            uncovered.push({ from: new Date(reached), to: new Date(span.from) });
        const start = Math.max(span.from, reached);
        if (span.to > start)
            covered += span.to - start;
        reached = Math.max(reached, span.to);
    }
    if (reached < window.to.getTime()) {
        uncovered.push({ from: new Date(reached), to: window.to });
    }
    return {
        windowMinutes: Math.round((window.to.getTime() - window.from.getTime()) / MINUTE_MS),
        coveredMinutes: Math.round(covered / MINUTE_MS),
        complete: uncovered.length === 0,
        uncovered,
    };
}
/** The level of one interval and the factors that raised it. A `null` factor does not trigger. */
function judgeInterval(interval, policy) {
    const factors = new Set();
    let level = "NONE";
    const raise = (factor, to) => {
        factors.add(factor);
        level = higher(level, to);
    };
    const probability = interval.precipProbabilityPct;
    if (probability !== null) {
        if (probability >= policy.high.precipProbabilityPct)
            raise("PRECIP_PROBABILITY", "HIGH");
        else if (probability >= policy.watch.precipProbabilityPct)
            raise("PRECIP_PROBABILITY", "WATCH");
    }
    const rate = hourlyRainOf(interval, policy);
    if (rate !== null && rate >= policy.high.precipMmPerHour)
        raise("PRECIP_AMOUNT", "HIGH");
    const gust = interval.gustKph;
    if (gust !== null) {
        if (gust >= policy.severe.gustKph)
            raise("WIND_GUST", "SEVERE");
        else if (gust >= policy.high.gustKph)
            raise("WIND_GUST", "HIGH");
        else if (gust >= policy.watch.gustKph)
            raise("WIND_GUST", "WATCH");
    }
    const stormProbability = interval.thunderstormProbabilityPct;
    if (stormProbability !== null) {
        if (stormProbability >= policy.severe.thunderstormProbabilityPct) {
            raise("THUNDERSTORM", "SEVERE");
        }
        else if (stormProbability >= policy.high.thunderstormProbabilityPct) {
            raise("THUNDERSTORM", "HIGH");
        }
    }
    if (interval.thunderstorm === true || interval.summaryCode === "THUNDERSTORM") {
        raise("THUNDERSTORM", "HIGH");
    }
    if (interval.summaryCode === "HAIL")
        raise("HAIL", "SEVERE");
    return {
        level,
        factors: [...factors],
        rate,
        known: {
            PRECIP_PROBABILITY: probability !== null,
            PRECIP_AMOUNT: rate !== null,
            WIND_GUST: gust !== null,
            THUNDERSTORM: stormProbability !== null ||
                interval.thunderstorm !== null ||
                interval.summaryCode !== "UNKNOWN",
        },
    };
}
/**
 * An interval is usable when it can show a risk (its level is above NONE), or when it knows at least one magnitude of the
 * weather (a probability, an hourly rain amount or a gust). Knowing only that there is no thunderstorm certifies nothing: a
 * six-hour interval with a calm symbol and no hourly number is not a calm forecast.
 */
const hasDecidingData = (verdict) => verdict.level !== "NONE" ||
    verdict.known.PRECIP_PROBABILITY ||
    verdict.known.PRECIP_AMOUNT ||
    verdict.known.WIND_GUST;
const maxOf = (values) => {
    const known = values.filter((value) => value !== null);
    return known.length === 0 ? null : Math.max(...known);
};
const minOf = (values) => {
    const known = values.filter((value) => value !== null);
    return known.length === 0 ? null : Math.min(...known);
};
/**
 * The weather risk of one item against a forecast (contract 5.2). Only the intervals that overlap the item's window count. The
 * highest level of any such interval wins. A factor that is `null` in every usable interval is listed in `unknownFactors`.
 * An interval with no deciding data is not usable (see `hasDecidingData`). With no usable interval the answer is `UNVERIFIED`;
 * an out-of-range forecast is `OUT_OF_RANGE`. An item that does not react to weather is `NOT_SENSITIVE`.
 *
 * COVERAGE. The usable intervals must cover the WHOLE activity window (their union). A known risk is kept whatever the coverage
 * (a HIGH hour inside a half-covered activity is still HIGH), but a calm answer is never certified for a window with an
 * uncovered period: it is `UNVERIFIED` `PARTIAL_COVERAGE`, and the answer says what was covered.
 */
export function assessWeatherRisk(input) {
    const policy = input.policy ?? WEATHER_POLICY;
    if (input.item.sensitivity === "NONE") {
        return Object.freeze({
            state: "NOT_SENSITIVE",
            level: "NONE",
            factors: [],
            unknownFactors: [],
            outcome: "NONE",
            reason: null,
            summary: null,
            coverage: null,
        });
    }
    if (input.forecast.kind === "OUT_OF_RANGE")
        return weatherUnverified("OUT_OF_RANGE", "OUT_OF_RANGE");
    const window = weatherWindowOf(input.item, policy);
    if (window === null)
        return weatherUnverified("NO_COVERAGE");
    const overlapping = input.forecast.intervals.filter((interval) => interval.start.getTime() < window.to.getTime() &&
        interval.end.getTime() > window.from.getTime());
    if (overlapping.length === 0)
        return weatherUnverified("NO_COVERAGE");
    const judged = overlapping.map((interval) => ({
        interval,
        verdict: judgeInterval(interval, policy),
    }));
    const usable = judged.filter(({ verdict }) => hasDecidingData(verdict));
    if (usable.length === 0)
        return weatherUnverified("NO_DECIDING_DATA");
    let level = "NONE";
    const triggered = new Set();
    let worst = usable[0];
    for (const entry of usable) {
        if (LEVEL_RANK[entry.verdict.level] > LEVEL_RANK[worst.verdict.level])
            worst = entry;
        level = higher(level, entry.verdict.level);
        for (const factor of entry.verdict.factors)
            triggered.add(factor);
    }
    const unknown = ["PRECIP_PROBABILITY", "PRECIP_AMOUNT", "WIND_GUST", "THUNDERSTORM"].filter((factor) => usable.every(({ verdict }) => !verdict.known[factor]));
    const intervals = usable.map(({ interval }) => interval);
    const coverage = coverageOf(window, intervals);
    // A calm answer needs the whole activity covered. Known risk is kept even when part of the window is unknown.
    if (level === "NONE" && !coverage.complete) {
        return weatherUnverified("PARTIAL_COVERAGE", "UNVERIFIED", coverage);
    }
    const temperatures = intervals.flatMap((interval) => [interval.tempMinC, interval.tempMaxC]);
    const summary = Object.freeze({
        maxPrecipProbabilityPct: maxOf(intervals.map((interval) => interval.precipProbabilityPct)),
        maxPrecipMmPerHour: maxOf(usable.map(({ verdict }) => verdict.rate)),
        maxWindKph: maxOf(intervals.map((interval) => interval.windKph)),
        maxGustKph: maxOf(intervals.map((interval) => interval.gustKph)),
        minTempC: minOf(temperatures),
        maxTempC: maxOf(temperatures),
        summaryCode: worst.interval.summaryCode,
    });
    return Object.freeze({
        state: "VERIFIED",
        level,
        factors: [...triggered].sort((a, b) => (FACTOR_ORDER.get(a) ?? 0) - (FACTOR_ORDER.get(b) ?? 0)),
        unknownFactors: unknown,
        outcome: weatherOutcomeOf(level),
        reason: null,
        summary,
        coverage,
    });
}
/**
 * The validation issue of an assessment (all WARNING, contract 3.4): `WEATHER_RISK_HIGH`, `WEATHER_RISK_SEVERE` (it forces a
 * concierge review), or `WEATHER_UNVERIFIED` for an answer that could not be given. `NONE` and `WATCH` raise no issue: a watch
 * is an information note on the evidence.
 */
export function weatherIssueCodeOf(assessment) {
    if (assessment.state === "NOT_SENSITIVE")
        return null;
    if (assessment.state !== "VERIFIED")
        return "WEATHER_UNVERIFIED";
    if (assessment.level === "SEVERE")
        return "WEATHER_RISK_SEVERE";
    if (assessment.level === "HIGH")
        return "WEATHER_RISK_HIGH";
    return null;
}
/** The local hours inside which a moved item may start and must end (a proposal never schedules the night). */
export const WEATHER_PROPOSAL_DAY = Object.freeze({ startHour: 8, endHour: 21, stepMinutes: 30 });
const SWAPPABLE_KINDS = new Set([
    "CATALOG_ITEM",
    "EXTERNAL_PLACE",
    "CUSTOM_ACTIVITY",
]);
/** The wall time of an instant in a zone, with the disambiguation that reads back the same instant (an overlap needs one). */
function wallTimeOf(instant, zone) {
    const text = localDateTimeOf(instant, zone);
    const date = text.slice(0, 10);
    const time = text.slice(11, 16);
    for (const disambiguation of ["reject", "earlier", "later"]) {
        try {
            if (zonedWallTimeToInstant(date, time, zone, disambiguation).instant.getTime() ===
                instant.getTime()) {
                return disambiguation === "reject"
                    ? { date, time, timeZone: zone }
                    : { date, time, timeZone: zone, disambiguation };
            }
        }
        catch {
            // The next disambiguation, then give up.
        }
    }
    return null;
}
function spanOf(item) {
    const window = weatherWindowOf(item);
    if (window === null || item.startsAt === null)
        return null;
    const length = item.endsAt !== null
        ? item.endsAt.getTime() - item.startsAt.getTime()
        : item.durationMinutes !== null
            ? item.durationMinutes * MINUTE_MS
            : WEATHER_POLICY.defaultItemMinutes * MINUTE_MS;
    return { from: item.startsAt.getTime(), to: item.startsAt.getTime() + Math.max(length, 1) };
}
const overlaps = (a, b) => a.from < b.to && b.from < a.to;
/**
 * The free slot of the same local day, nearest to the flagged item's start, where the forecast is KNOWN and calm (NONE or
 * WATCH), the item keeps its length, ends by the day's last hour and overlaps no other item of its section.
 */
function calmerSlotOf(flagged, items, flaggedSpan, riskAt) {
    const zone = flagged.timeZone;
    const date = localDateTimeOf(flagged.startsAt, zone).slice(0, 10);
    const length = flaggedSpan.to - flaggedSpan.from;
    const others = items
        .filter((item) => item.stableId !== flagged.stableId && item.sectionKey === flagged.sectionKey)
        .map(spanOf)
        .filter((span) => span !== null);
    const slots = [];
    const { startHour, endHour, stepMinutes } = WEATHER_PROPOSAL_DAY;
    const dayEnd = instantOf(date, `${String(endHour).padStart(2, "0")}:00`, zone);
    for (let minutes = startHour * 60; minutes < endHour * 60; minutes += stepMinutes) {
        const time = `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
        let from;
        try {
            from = zonedWallTimeToInstant(date, time, zone, "reject").instant;
        }
        catch {
            continue; // A gap or an overlap of the zone: not a slot to offer.
        }
        if (from.getTime() === flaggedSpan.from)
            continue;
        const candidate = { from: from.getTime(), to: from.getTime() + length };
        if (localDateTimeOf(new Date(candidate.to), zone).slice(0, 10) !== date)
            continue;
        if (candidate.to > dayEnd)
            continue;
        if (others.some((other) => overlaps(candidate, other)))
            continue;
        slots.push({ from: from.getTime(), wall: { date, time, timeZone: zone } });
    }
    slots.sort((a, b) => Math.abs(a.from - flaggedSpan.from) - Math.abs(b.from - flaggedSpan.from) || a.from - b.from);
    for (const slot of slots) {
        const risk = riskAt(new Date(slot.from), new Date(slot.from + length));
        if (risk === "NONE" || risk === "WATCH")
            return slot.wall;
    }
    return null;
}
/** The instant of a local time, or `-Infinity` when the zone has no such time (then no slot is offered that day). */
function instantOf(date, time, zone) {
    try {
        return zonedWallTimeToInstant(date, time, zone, "later").instant.getTime();
    }
    catch {
        return Number.NEGATIVE_INFINITY;
    }
}
/**
 * A ready-to-use proposal for an outdoor item with a HIGH risk. First choice: move ONLY the outdoor item to a calmer free slot of
 * the same day (`MOVE_TO_CALMER_SLOT`), when the forecast is known there. Second choice: swap its time with a valid indoor
 * alternative that is already in the plan (`SWAP_WITH_INDOOR`): a scheduled item of the same day section that does not react to
 * weather, is PLANNED, is not fixed and carries no request. Each item keeps its own length; nothing that would overlap a third
 * item is offered. The nearest in time wins, then the lower stable id. `null` when nothing fits. Only MOVE_ITEM operations are
 * produced: no item is removed, no id changes, nothing is cancelled. Pure; applies nothing.
 *
 * KNOWN CALM REQUIRED. Both strategies need `riskAt` to say `NONE` or `WATCH` for the slot the flagged item would take, over the
 * WHOLE slot. An unknown answer (`null`: no forecast of that slot, a partly covered slot, a reused reading with no hours) is not
 * a calm answer, and `riskAt` absent means nothing is known: no proposal. So a check never proposes a move that another check of
 * the same plan would call stormy.
 */
export function proposeWeatherAlternative(input) {
    const { flagged } = input;
    const flaggedSpan = spanOf(flagged);
    if (flaggedSpan === null || flagged.startsAt === null || flagged.timeZone === null)
        return null;
    const slot = calmerSlotOf(flagged, input.items, flaggedSpan, input.riskAt);
    if (slot !== null) {
        return Object.freeze({
            strategy: "MOVE_TO_CALMER_SLOT",
            flaggedStableId: flagged.stableId,
            swapWithStableId: null,
            operations: [{ op: "MOVE_ITEM", stableId: flagged.stableId, start: slot }],
        });
    }
    const candidates = input.items
        .filter((item) => item.stableId !== flagged.stableId &&
        item.sectionKey === flagged.sectionKey &&
        item.sensitivity === "NONE" &&
        item.itemStatus === "PLANNED" &&
        !item.isFixed &&
        !item.carriesRequest &&
        SWAPPABLE_KINDS.has(item.kind) &&
        item.startsAt !== null &&
        item.timeZone !== null)
        .map((item) => ({ item, span: spanOf(item) }))
        .filter((entry) => entry.span !== null)
        .sort((a, b) => Math.abs(a.span.from - flaggedSpan.from) - Math.abs(b.span.from - flaggedSpan.from) ||
        (a.item.stableId < b.item.stableId ? -1 : 1));
    const lengthOf = (span) => span.to - span.from;
    for (const { item, span } of candidates) {
        const flaggedAfter = { from: span.from, to: span.from + lengthOf(flaggedSpan) };
        const indoorAfter = { from: flaggedSpan.from, to: flaggedSpan.from + lengthOf(span) };
        if (overlaps(flaggedAfter, indoorAfter))
            continue;
        const others = input.items.filter((other) => other.stableId !== flagged.stableId &&
            other.stableId !== item.stableId &&
            other.sectionKey === flagged.sectionKey);
        const clash = others.some((other) => {
            const otherSpan = spanOf(other);
            return (otherSpan !== null &&
                (overlaps(flaggedAfter, otherSpan) || overlaps(indoorAfter, otherSpan)));
        });
        if (clash)
            continue;
        const risk = input.riskAt(new Date(flaggedAfter.from), new Date(flaggedAfter.to));
        if (risk !== "NONE" && risk !== "WATCH")
            continue; // Unknown is not calm.
        const flaggedStart = wallTimeOf(item.startsAt, item.timeZone);
        const indoorStart = wallTimeOf(flagged.startsAt, flagged.timeZone);
        if (flaggedStart === null || indoorStart === null)
            continue;
        return Object.freeze({
            strategy: "SWAP_WITH_INDOOR",
            flaggedStableId: flagged.stableId,
            swapWithStableId: item.stableId,
            operations: [
                { op: "MOVE_ITEM", stableId: flagged.stableId, start: flaggedStart },
                { op: "MOVE_ITEM", stableId: item.stableId, start: indoorStart },
            ],
        });
    }
    return null;
}
